import React, { useState } from 'react';
import styled from 'styled-components';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { BreakpointContext, useBreakpoints } from 'react-use-breakpoints';
import { rem } from 'polished';
import { Text } from '../Text';
import { Content } from '../Content';
import { MdContent } from '../MdContent';
import { Rectangle } from '../Rectangle';
import { RevealingBox } from '../RevealingBox';
import { Slider } from '../Slider';

interface IProcessesSlider {
  className?: string;
  processes: any;
}

export const StyledProcessesSlider = styled.div`
  .rectable--processes {
    padding: ${rem(`16px`)};
    ${(props) => props.theme.breakpoints.desktop`
      padding: ${rem(`36px`)};
    `}
  }
  .processes-slider {
    margin-bottom: 0;
    :after {
      height: ${rem(`56px`)};
    }
    .dots {
      background-color: transparent;
      padding: ${rem(`12px`)} 0;
      margin: 0 ${rem(`-6px`)};
    }
    .slick-track {
      align-items: start;
      padding-top: 0;
      display: flex;
    }
    .previous {
      left: ${rem(`-6px`)};
    }
    .next {
      right: ${rem(`-6px`)};
    }
    .slick-slider .slick-list {
      margin: 0;
      padding: 0;
    }
    ${(props) => props.theme.breakpoints.phablet`
      :after {
        height: ${rem(`72px`)};
      }
      .process-slider {
        margin-bottom: ${rem(`48px`)};
      }
      .dots {
        padding: 0;
        margin: 0;
      }
      .slick-track {
        align-items: center;
      }
      .previous {
        left: 0;
      }
      .next {
        right: 0;
      }
    `}
    ${(props) => props.theme.breakpoints.tablet`
      :after {
        height: ${rem(`56px`)};
      }
      .dots__list li {
        height:  ${rem(`24px`)};
      }
      .previous,
      .next {
        top: calc(100% + 4px);
      }
    `}
    ${(props) => props.theme.breakpoints.desktop`
      :after {
        height: ${rem(`72px`)};
      }
      .dots__list li {
        height:  ${rem(`48px`)};
      }
      .previous,
      .next {
        top: calc(100% + 9px);
      }
    `}
  }
  .body1 {
    margin: 0;
  }
`;

interface StyledProcessesSliderContentProps {
  animation: boolean;
}

export const StyledProcessesSliderContent = styled.div<StyledProcessesSliderContentProps>`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-gap: ${rem(`24px`)};
  ${({ theme, animation }) => theme.breakpoints.phablet`
      grid-gap: ${rem(`48px`)};
      grid-template-columns: ${animation ? `repeat(1, 1fr)` : `repeat(2, 1fr)`};
    `}
  .process-slider__animation {
    display: flex;
    align-items: center;
    justify-content: center;
    svg {
      height: ${rem(`400px`)};
      width: ${rem(`254px`)};
      ${({ theme }) => theme.breakpoints.phablet`
        height: auto;
        width: auto;
        min-width: 100%;
        min-height: ${rem(`235px`)};
      `}
      ${({ theme }) => theme.breakpoints.tablet`
        min-height: ${rem(`254px`)};
      `}
      ${({ theme }) => theme.breakpoints.desktop`
        min-height: ${rem(`378px`)};
      `}
    }
  }
  .process-slider__image {
    display: none;
    ${({ theme }) => theme.breakpoints.phablet`
      display: flex;
      align-items: center;
      justify-content: center;
    `}
  }
`;

const ProcessesSliderContent = ({
  name,
  title,
  subtitle,
  body,
  parsedImage,
  animation,
}) => (
  <StyledProcessesSliderContent animation={Boolean(animation)}>
    {animation ? (
      <div
        className="process-slider__animation"
        dangerouslySetInnerHTML={{
          __html: animation,
        }}
      />
    ) : (
      <>
        <Content noMargin>
          <Text type="title3" tag="h2" color="gray_3">
            {title}
          </Text>
          <Text type="subtitle1" tag="h3" color="gray_3_65">
            {subtitle}
          </Text>
          <Text type="body1" tag="div" color="gray_3">
            <MdContent content={body?.body} />
          </Text>
        </Content>
        <div className="process-slider__image">
          <GatsbyImage
            image={parsedImage}
            className="slick-slide-image"
            alt={`Image of ${name}`}
          />
        </div>
      </>
    )}
  </StyledProcessesSliderContent>
);

export const ProcessesSlider = ({ processes, className }: IProcessesSlider) => {
  const breakpoints = React.useContext(BreakpointContext);
  const { breakpoint } = useBreakpoints(breakpoints);
  const isUnderPhablet = [`watch`, `phone`].some((item) => item === breakpoint);
  const [slide, setSlide] = useState(0);
  const sortedProcesses = processes.sort((a: any, b: any) => a.order - b.order);

  const settingsSlider = {
    slidesToShow: 1,
    slidesToScroll: 1,
    infinite: false,
  };

  const settingsSliderPhoneResponsive = {
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  const settingsSliderPhabletResponsive = {
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  const settingsSliderTabletResponsive = {
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  return (
    <StyledProcessesSlider className={className}>
      <Rectangle className="rectangle--pad-36 rectable--processes">
        <Slider
          className="processes-slider"
          settings={settingsSlider}
          titles={sortedProcesses.map(({ name }) => name)}
          responsivePhoneSettings={settingsSliderPhoneResponsive}
          responsivePhabletSettings={settingsSliderPhabletResponsive}
          responsiveTabletSettings={settingsSliderTabletResponsive}
          paginationMargin="24px"
          onChange={(currentSlide) => {
            setSlide(currentSlide);
          }}
        >
          {sortedProcesses.map(
            (
              {
                image,
                displayId,
                name,
                title,
                subtitle,
                body,
                animationDesktop,
                animationMobile,
              },
              idx: number,
            ) => {
              const parsedImage = getImage(image);
              const animation = isUnderPhablet
                ? animationMobile?.svg?.originalContent
                : animationDesktop?.svg?.originalContent;
              return isUnderPhablet ? (
                <RevealingBox
                  height={402}
                  key={displayId}
                  value={slide === idx ? null : false}
                >
                  <ProcessesSliderContent
                    name={name}
                    title={title}
                    subtitle={subtitle}
                    body={body}
                    parsedImage={parsedImage}
                    animation={animation}
                  />
                </RevealingBox>
              ) : (
                <ProcessesSliderContent
                  name={name}
                  title={title}
                  subtitle={subtitle}
                  body={body}
                  key={displayId}
                  parsedImage={parsedImage}
                  animation={animation}
                />
              );
            },
          )}
        </Slider>
      </Rectangle>
    </StyledProcessesSlider>
  );
};
